import { ChangeEvent, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faFileCirclePlus } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectActiveRobotId } from "@/store/ses/sesRobotSlice";
import { downloadFile, selectDownloadFile, selectUploadFile, uploadFile } from "@/store/ses/sesSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "@/constants/accessRights";
import { colorSecondary } from "@/constants/colors";
import { RequestStatus, ResponseStatus } from "@/types/statusTypes";
import { ProgressLinear } from "@/components/MUI";
import { ISendFileProps } from "./SendFile.props";
import styles from "./SendFile.module.scss";
import { IActionSendFile } from "@/types/ses/sesItemConfigTypes";

const SendFile = ({ action, idx, channel, isDisabled, handleSaveActionInStore }: ISendFileProps): JSX.Element => {
	const [inputFile, setInputFile] = useState<File | null>(null); // файл для загрузки

	const dispatch = useAppDispatch();
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const uploadFileStatus = useAppSelector(selectUploadFile); // store - статус загрузки файла
	const downloadFileStatus = useAppSelector(selectDownloadFile); // store - статус скачивания файла

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за статусом загрузки файла
	useEffect(() => {
		// если загрузка прошла успешно
		if (uploadFileStatus.status === RequestStatus.IDLE && uploadFileStatus.error === ResponseStatus.SUCCESS && uploadFileStatus.id) {
			inputFile && sendFileActionDataChangeHandler({ fileId: uploadFileStatus.id, fileName: inputFile.name }); // добавляем данные файла
			setInputFile(null);
		}
	}, [uploadFileStatus]);

	// обработчик загрузки файла
	const handleUploadFile = (e: ChangeEvent<HTMLInputElement>): void => {
		if (e.target.files && e.target.files.length > 0) {
			setInputFile(e.target.files[0]);
			const formData = new FormData();
			formData.append("file", e.target.files[0]);
			activeRobotId && dispatch(uploadFile({ robotId: activeRobotId, formData })); // загрузка файла
		} else {
			setInputFile(null);
		}
	};

	// обработчик повторной загрузки файла
	const handleReUploadFile = (): void => {
		const formData = new FormData();
		if (inputFile && activeRobotId) {
			formData.append("file", inputFile);
			dispatch(uploadFile({ robotId: activeRobotId, formData })); // загрузка файла
		}
	};

	// обработчик изменения данных действия "Отправить файл" (sendfile)
	const sendFileActionDataChangeHandler = ({ fileId, fileName }: { fileId: string; fileName: string }): void => {
		const data: { channel: string; idx: number; action: Partial<IActionSendFile> } = { channel, idx, action: { file_id: fileId, filename: fileName } };
		handleSaveActionInStore(data);
	};

	return (
		<div className={styles.container}>
			<div className={styles.top}>
				<input
					className={styles.fileInput}
					type="file"
					id={`sendfile${idx}`}
					onChange={handleUploadFile}
					disabled={isDisabled || uploadFileStatus.status === RequestStatus.LOADING || !isAccess(SES.UPLOAD_FILE)}
				/>

				<label htmlFor={`sendfile${idx}`} className={styles.fileLabel}>
					{/* название */}
					<div
						className={cn(styles.fileLabelName, {
							[styles.fileLabelNameNotAllow]: isDisabled || uploadFileStatus.status === RequestStatus.LOADING || !isAccess(SES.UPLOAD_FILE),
						})}
						title={inputFile?.name || (action.action === "sendfile" ? action.filename : "")}
					>
						{inputFile ? inputFile.name : action.action === "sendfile" && action.filename ? action.filename : translate("input_chooseFile")}
					</div>
					{/* иконка */}
					{!isDisabled && (
						<div
							className={cn(styles.fileLabelIcon, {
								[styles.fileLabelIconNotAllow]: uploadFileStatus.status === RequestStatus.LOADING || !isAccess(SES.UPLOAD_FILE),
							})}
							title={translate("input_chooseFile")}
						>
							<FontAwesomeIcon icon={faFileCirclePlus} color={colorSecondary} size="xl" />
						</div>
					)}
				</label>

				{/* скачивание */}
				{action.action === "sendfile" && action.file_id && isAccess(SES.DOWNLOAD_FILE) && (
					<button
						className={styles.downloadBtn}
						type="button"
						title={translate("button_download")}
						disabled={downloadFileStatus.status === RequestStatus.LOADING || uploadFileStatus.status === RequestStatus.LOADING}
						onClick={() => {
							activeRobotId && dispatch(downloadFile({ robotId: activeRobotId, fileId: action.file_id, fileName: action.filename }));
						}}
					>
						<FontAwesomeIcon icon={faDownload} color={colorSecondary} size="lg" />
					</button>
				)}
			</div>

			<div className={styles.bottom}>
				{/* прогресс отправки файла */}
				{action.action === "sendfile" && uploadFileStatus.status === RequestStatus.LOADING && <ProgressLinear value={uploadFileStatus.progress} />}

				{/* повторная загрузка файла */}
				{uploadFileStatus.status !== RequestStatus.LOADING && inputFile && (
					<div className={styles.error}>
						<span className={styles.errorText}>{translate("title_errorOccurred")}.</span>{" "}
						<span className={styles.errorReload} onClick={handleReUploadFile}>
							{translate("title_reload")}
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default SendFile;
